import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, Calendar, User, Clock, ArrowLeft, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchBlogPostBySlug, fetchBlogPosts, type BlogPost } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

function formatDate(dateString: string | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function estimateReadTime(content: string): string {
  const text = content.replace(/<[^>]+>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

const GRADIENT_PALETTES = [
  'from-[#635bff] to-[#00d4ff]',
  'from-[#00d4ff] to-[#00ff88]',
  'from-[#a855f7] to-[#ec4899]',
  'from-[#ff6b6b] to-[#ffa500]',
];

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPost() {
      if (!slug) return;
      try {
        setLoading(true);
        setError(null);
        const [fetched, { posts: allPosts }] = await Promise.all([
          fetchBlogPostBySlug(slug),
          fetchBlogPosts(100),
        ]);
        if (cancelled) return;

        if (!fetched) {
          setError('Blog post not found');
          setLoading(false);
          return;
        }

        setPost(fetched);

        // Pick up to 3 related posts from the same category (excluding current)
        const related = allPosts
          .filter(p => p.id !== fetched.id && p.categories.some(c => fetched.categories.includes(c)))
          .slice(0, 3);
        // If fewer than 3 category matches, fill with recent posts
        if (related.length < 3) {
          const remaining = allPosts
            .filter(p => p.id !== fetched.id && !related.some(r => r.id === p.id))
            .slice(0, 3 - related.length);
          related.push(...remaining);
        }
        setRelatedPosts(related);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load blog post');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPost();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Blog', item: '/blog' },
        { name: post?.title ?? 'Blog Post' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff]/20 via-transparent to-[#00d4ff]/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#635bff]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00d4ff]/15 rounded-full blur-3xl animate-float-delayed" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-6">
            <Link to="/resources" className="hover:text-slate-900 dark:hover:text-white transition-colors">Resources</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-slate-900 dark:hover:text-white transition-colors">Blog</Link>
            {post && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span className="text-slate-900 dark:text-white truncate max-w-xs">{post.title}</span>
              </>
            )}
          </div>

          {!loading && post && (
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {post.categories.map(cat => (
                  <span
                    key={cat}
                    className="inline-block px-4 py-1.5 text-sm font-medium text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full"
                  >
                    {cat}
                  </span>
                ))}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
                  {post.excerpt.replace(/&hellip;/g, '...').replace(/<[^>]+>/g, '').slice(0, 200)}
                </p>
              )}
              <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-slate-600 dark:text-slate-400">
                {post.author && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{post.author}</span>
                  </div>
                )}
                {post.publish_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(post.publish_date)}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{estimateReadTime(post.content)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-8 h-8 text-[#635bff] animate-spin" />
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading blog post...</span>
            </div>
          ) : error ? (
            <div className="text-center py-32">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={() => navigate('/resources')}
                className="px-4 py-2 bg-[#635bff] text-white rounded-lg hover:bg-[#635bff]/90 transition-colors"
              >
                Back to Resources
              </button>
            </div>
          ) : post ? (
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {/* Featured Image */}
                {post.featured_image && (
                  <div className="mb-10 rounded-2xl overflow-hidden">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                )}

                <div className="bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl p-8 lg:p-12">
                  <div
                    className="prose dark:prose-invert prose-lg max-w-none
                      prose-headings:text-slate-900 dark:prose-headings:text-white prose-headings:font-semibold
                      prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                      prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                      prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-p:leading-relaxed
                      prose-ul:text-slate-700 dark:prose-ul:text-slate-300 prose-li:marker:text-[#635bff]
                      prose-a:text-[#635bff] prose-a:no-underline hover:prose-a:underline
                      prose-img:rounded-xl prose-img:mx-auto
                      prose-video:rounded-xl prose-video:mx-auto prose-video:max-w-full"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                </div>

                {/* Post Footer */}
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-4">
                    {post.author && (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-[#635bff] to-[#00d4ff] rounded-full flex items-center justify-center text-xs font-medium text-white">
                          {post.author.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <span className="font-medium text-slate-900 dark:text-white">{post.author}</span>
                      </div>
                    )}
                    {post.publish_date && <span>{formatDate(post.publish_date)}</span>}
                  </div>
                  <button
                    onClick={() => navigate('/blog')}
                    className="flex items-center gap-2 text-[#635bff] hover:text-[#5449e6] font-medium transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Blog
                  </button>
                </div>
              </div>

              {/* Right Sidebar - More from the blog */}
              {relatedPosts.length > 0 && (
                <div className="lg:col-span-1">
                  <div className="sticky top-24 space-y-6">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">More from the blog</h3>
                    {relatedPosts.map((rp, idx) => (
                      <Link
                        key={rp.id}
                        to={`/blog/${rp.slug}`}
                        className="group block bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden hover:bg-slate-100 dark:hover:bg-white/10 hover:border-[#635bff]/30 transition-all"
                      >
                        <div className="aspect-[16/9] relative overflow-hidden">
                          {rp.featured_image ? (
                            <img
                              src={rp.featured_image}
                              alt={rp.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className={`w-full h-full bg-gradient-to-br ${GRADIENT_PALETTES[idx % GRADIENT_PALETTES.length]} flex items-center justify-center p-4`}>
                              <span className="text-white/90 text-sm font-semibold text-center line-clamp-3">{rp.title}</span>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] line-clamp-2 transition-colors">
                            {rp.title}
                          </h4>
                          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                            {rp.excerpt.replace(/&hellip;/g, '...').replace(/<[^>]+>/g, '').slice(0, 100)}
                          </p>
                          <p className="mt-2 text-xs text-slate-500">{formatDate(rp.publish_date)}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
